import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import RichText from "@/components/RichText";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import { ChevronLeft, ListChecks, Plus, Pencil, Copy, Eye, FileSearch, History } from "lucide-react";

const ENTITY_TYPES = [["limited_company", "Limited Company"], ["llp", "LLP"], ["partnership", "Partnership"], ["sole_trader", "Sole Trader"], ["individual", "Individual"]];
const ENTITY_NAME = { all: "All", ...Object.fromEntries(ENTITY_TYPES) };
const CLS = { identical: "bg-emerald-50 text-emerald-700 border-emerald-200", similar: "bg-amber-50 text-amber-700 border-amber-200", different: "bg-red-50 text-red-700 border-red-200", unmapped: "bg-slate-50 text-slate-600 border-slate-200" };
const blank = () => ({ name: "", heading: "", content: "", order: 100, active: true, entities: ["all"] });
const entsOf = (b) => (b.entities && b.entities.length ? b.entities : (b.entity === "company" ? ["limited_company", "llp", "partnership"] : b.entity === "individual" ? ["sole_trader", "individual"] : ["all"]));

export default function RequirementBlocks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["super_admin", "pricing_admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [previewBlock, setPreviewBlock] = useState(null);
  const [usedBy, setUsedBy] = useState(null);
  const [audit, setAudit] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [versions, setVersions] = useState(null); // array when history open
  const [compareVer, setCompareVer] = useState(null);
  const [bulkEntOpen, setBulkEntOpen] = useState(false);
  const [bulkEnts, setBulkEnts] = useState([]);

  const load = () => api.get("/requirement-blocks").then((r) => setRows(r.data)).catch(() => toast.error("Could not load blocks"));
  useEffect(() => { load(); }, []);

  const toggleEnt = (v) => setForm((f) => {
    let e = new Set(f.entities || []);
    if (v === "all") return { ...f, entities: ["all"] };
    e.delete("all"); e.has(v) ? e.delete(v) : e.add(v);
    return { ...f, entities: e.size ? [...e] : ["all"] };
  });
  const openNew = () => { setEditId(null); setForm(blank()); setVersions(null); setCompareVer(null); setOpen(true); };
  const openEdit = (b) => { setEditId(b.id); setForm({ name: b.name || "", heading: b.heading || "", content: b.content || "", order: b.order ?? 100, active: !!b.active, entities: entsOf(b) }); setVersions(null); setCompareVer(null); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the block a name"); return; }
    setBusy(true);
    try {
      if (editId) await api.put(`/requirement-blocks/${editId}`, form);
      else await api.post("/requirement-blocks", form);
      toast.success(editId ? "Block updated" : "Block created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const duplicate = async (b) => { try { await api.post(`/requirement-blocks/${b.id}/duplicate`); toast.success("Block duplicated (inactive)"); load(); } catch { toast.error("Could not duplicate"); } };
  const toggleActive = async (b) => {
    if (b.active && b.used_by > 0) {
      const { data } = await api.get(`/requirement-blocks/${b.id}/services`);
      const names = data.map((s) => s.client_name).join(", ");
      if (!(await confirmDialog({ title: "Deactivate block in use?", description: `“${b.name}” is mapped to ${b.used_by} active service(s): ${names}. Deactivating removes it from future letters for those services.`, tone: "destructive", confirmText: "Deactivate" }))) return;
    }
    try { await api.put(`/requirement-blocks/${b.id}`, { ...b, entities: entsOf(b), active: !b.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const showUsedBy = async (b) => { try { const { data } = await api.get(`/requirement-blocks/${b.id}/services`); setUsedBy({ block: b, services: data }); } catch { toast.error("Could not load services"); } };
  const openAudit = async () => { try { const { data } = await api.get("/clearance/migration-audit"); setAudit(data); } catch { toast.error("Could not load audit"); } };
  const openHistory = async () => { if (!editId) return; setCompareVer(null); try { const { data } = await api.get(`/requirement-blocks/${editId}/versions`); setVersions(data); } catch { toast.error("Could not load history"); } };
  const restoreVersion = async (v) => {
    if (!(await confirmDialog({ title: "Restore this version?", description: "The block content will be replaced with this saved version.", confirmText: "Restore" }))) return;
    try { const { data } = await api.post(`/requirement-blocks/${editId}/versions/${v.id}/restore`); setForm({ name: data.name || "", heading: data.heading || "", content: data.content || "", order: data.order ?? 100, active: !!data.active, entities: entsOf(data) }); toast.success("Version restored"); setVersions(null); load(); }
    catch { toast.error("Could not restore"); }
  };

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulk = async (action, entities) => {
    const verb = action === "activate" ? "activated" : action === "deactivate" ? "deactivated" : "updated";
    try { await api.post("/requirement-blocks/bulk", { ids: [...sel], action, entities }); toast.success(`${sel.size} block(s) ${verb}`); setSel(new Set()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };
  const toggleBulkEnt = (v) => setBulkEnts((cur) => {
    if (v === "all") return [];
    const e = new Set(cur.filter((x) => x !== "all"));
    e.has(v) ? e.delete(v) : e.add(v);
    return [...e];
  });
  const applyBulkEnts = async () => { await bulk("set_entities", bulkEnts.length ? bulkEnts : ["all"]); setBulkEntOpen(false); };

  return (
    <div>
      <PageHeader title="Requirement Blocks" accent="teal" Icon={ListChecks}
        subtitle="Reusable clearance wording held centrally and mapped to services. Edit wording here once; every service and letter that uses a block stays consistent."
        actions={<div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openAudit} data-testid="rb-audit"><FileSearch className="w-4 h-4 mr-1" />Migration audit</Button>
          {canEdit && <Button size="sm" className="bg-cass-navy text-white" onClick={openNew} data-testid="rb-new"><Plus className="w-4 h-4 mr-1" />New block</Button>}
          <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="rb-back"><ChevronLeft className="w-4 h-4 mr-1" />Settings</Button>
        </div>} />
      <div className="p-4 md:p-8 max-w-[1000px] space-y-2">
        {canEdit && sel.size > 0 && (
          <div className="flex items-center gap-2 bg-cass-navy/5 border border-cass-navy/15 rounded-lg p-2 mb-1" data-testid="rb-bulk-bar">
            <span className="text-xs text-cass-navy font-medium ml-1">{sel.size} selected</span>
            <Button size="sm" variant="outline" className="h-7" onClick={() => bulk("activate")} data-testid="rb-bulk-activate">Activate</Button>
            <Button size="sm" variant="outline" className="h-7" onClick={() => bulk("deactivate")} data-testid="rb-bulk-deactivate">Deactivate</Button>
            <Button size="sm" variant="outline" className="h-7" onClick={() => bulk("set_entities", ["all"])} data-testid="rb-bulk-all">Set: All entities</Button>
            <Button size="sm" variant="outline" className="h-7" onClick={() => { setBulkEnts([]); setBulkEntOpen(true); }} data-testid="rb-bulk-entities">Set entities…</Button>
            <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={() => setSel(new Set())}>Clear</Button>
          </div>
        )}
        <div data-testid="rb-list" className="space-y-2">
          {rows.length === 0 && <p className="text-sm text-cass-slate">No requirement blocks yet.</p>}
          {rows.map((b) => (
            <Card key={b.id} className="p-4 rounded-xl flex items-start justify-between gap-3" data-testid={`rb-row-${b.id}`}>
              <div className="flex items-start gap-3 min-w-0">
                {canEdit && <Checkbox checked={sel.has(b.id)} onCheckedChange={() => toggleSel(b.id)} className="mt-1" data-testid={`rb-sel-${b.id}`} />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-alt font-semibold text-cass-navy truncate">{b.name}</h3>
                    {!b.active && <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">Inactive</Badge>}
                    <span className="text-xs text-cass-slate">Order {b.order}</span>
                  </div>
                  <div className="text-xs text-cass-slate mt-1">Applies to: <span className="text-cass-navy">{entsOf(b).map((e) => ENTITY_NAME[e] || e).join(", ")}</span></div>
                  <button className="text-xs text-cass-teal underline mt-1" onClick={() => showUsedBy(b)} data-testid={`rb-usedby-${b.id}`}>Used by {b.used_by} service{b.used_by === 1 ? "" : "s"}</button>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Switch checked={!!b.active} onCheckedChange={() => canEdit && toggleActive(b)} disabled={!canEdit} data-testid={`rb-toggle-${b.id}`} />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setPreviewBlock(b)} data-testid={`rb-preview-${b.id}`}><Eye className="w-4 h-4" /></Button>
                {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(b)} data-testid={`rb-edit-${b.id}`}><Pencil className="w-4 h-4" /></Button>}
                {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => duplicate(b)} data-testid={`rb-dup-${b.id}`}><Copy className="w-4 h-4" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rb-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit block" : "New block"}</DialogTitle><DialogDescription>Wording is shared by every service mapped to this block. Inactive or empty blocks are excluded from generated letters.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Block name (internal)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rb-name" /></div>
              <div><Label className="text-xs">Letter heading</Label><Input value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} data-testid="rb-heading" /></div>
            </div>
            <div><Label className="text-xs">Display order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="w-32" data-testid="rb-order" /></div>
            <div>
              <Label className="text-xs">Applies to (entity types)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="rb-entities">
                {[["all", "All"], ...ENTITY_TYPES].map(([v, l]) => {
                  const on = (form.entities || []).includes(v);
                  return <button key={v} type="button" onClick={() => toggleEnt(v)} data-testid={`rb-ent-${v}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-cass-navy text-white border-cass-navy" : "bg-white text-cass-slate border-border hover:border-cass-navy"}`}>{l}</button>;
                })}
              </div>
            </div>
            <div><Label className="text-xs">Requirement content</Label><RichText value={form.content} onChange={(v) => setForm({ ...form, content: v })} testId="rb-content" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="rb-active" /><span className="text-sm text-cass-slate">Active</span></div>
            {editId && (
              <div className="border-t border-border pt-2">
                <Button type="button" size="sm" variant="outline" className="h-7" onClick={openHistory} data-testid="rb-history"><History className="w-3.5 h-3.5 mr-1" />Version history</Button>
                {versions && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto" data-testid="rb-history-list">
                    {versions.length === 0 && <p className="text-xs text-cass-slate">No versions yet.</p>}
                    {versions.map((v) => (
                      <div key={v.id} className="flex items-center justify-between text-xs border border-border rounded px-2 py-1">
                        <span className="text-cass-slate">{v.saved_at ? new Date(v.saved_at).toLocaleString("en-GB") : ""} · {v.saved_by}</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant={compareVer?.id === v.id ? "default" : "ghost"} className={compareVer?.id === v.id ? "bg-cass-navy text-white h-6" : "h-6"} onClick={() => setCompareVer(compareVer?.id === v.id ? null : v)} data-testid={`rb-compare-${v.id}`}>Compare</Button>
                          <Button size="sm" variant="ghost" className="h-6" onClick={() => restoreVersion(v)} data-testid={`rb-restore-${v.id}`}>Restore</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {versions && compareVer && (
                  <div className="grid grid-cols-2 gap-2 mt-2 border-t border-border pt-2" data-testid="rb-compare-view">
                    <div>
                      <div className="text-[11px] font-semibold text-cass-navy mb-1">Current draft</div>
                      <div className="text-xs font-semibold text-cass-slate">{form.heading || form.name}</div>
                      <div className="prose prose-sm max-w-none border border-border rounded p-2 bg-white max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: form.content || "<em>(empty)</em>" }} />
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold text-cass-navy mb-1">Selected version <span className="font-normal text-cass-slate">· {compareVer.saved_at ? new Date(compareVer.saved_at).toLocaleString("en-GB") : ""}</span></div>
                      <div className="text-xs font-semibold text-cass-slate">{compareVer.heading || compareVer.name}</div>
                      <div className="prose prose-sm max-w-none border border-border rounded p-2 bg-cass-bone/30 max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: compareVer.content || "<em>(empty)</em>" }} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-cass-navy text-white" onClick={save} disabled={busy} data-testid="rb-save">{editId ? "Save changes" : "Create block"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewBlock} onOpenChange={(o) => !o && setPreviewBlock(null)}>
        <DialogContent className="max-w-lg" data-testid="rb-preview-dialog">
          <DialogHeader><DialogTitle>{previewBlock?.heading || previewBlock?.name}</DialogTitle><DialogDescription>Preview of this block's requirement content.</DialogDescription></DialogHeader>
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewBlock?.content || "<em>(empty — add wording to include it in letters)</em>" }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!usedBy} onOpenChange={(o) => !o && setUsedBy(null)}>
        <DialogContent className="max-w-md" data-testid="rb-usedby-dialog">
          <DialogHeader><DialogTitle>Services using “{usedBy?.block?.name}”</DialogTitle><DialogDescription>Active services mapped to this block.</DialogDescription></DialogHeader>
          {usedBy?.services?.length ? (
            <ul className="list-disc ml-5 text-sm text-cass-navy">{usedBy.services.map((s) => <li key={s.id}>{s.client_name}</li>)}</ul>
          ) : <p className="text-sm text-cass-slate">No active services use this block yet.</p>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!audit} onOpenChange={(o) => !o && setAudit(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="rb-audit-dialog">
          <DialogHeader><DialogTitle>Migration audit</DialogTitle><DialogDescription>How each service's legacy wording compares to the central block(s) now mapped to it. Review “similar” and “different” rows before retiring the old wording.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {(audit || []).length === 0 && <p className="text-sm text-cass-slate">No legacy per-service wording found — nothing to reconcile.</p>}
            {(audit || []).map((r) => (
              <div key={r.service_id} className="border border-border rounded-md p-3" data-testid={`rb-audit-row-${r.service_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-cass-navy text-sm">{r.service_name}</span>
                  <Badge variant="outline" className={CLS[r.classification] || CLS.unmapped}>{r.classification}{r.classification !== "unmapped" ? ` · ${Math.round(r.ratio * 100)}%` : ""}</Badge>
                </div>
                <div className="text-xs text-cass-slate mt-1">Mapped: {r.mapped_blocks.length ? r.mapped_blocks.join(", ") : "— none —"}</div>
                <div className="text-[11px] text-cass-slate mt-1 italic line-clamp-2">Legacy: {r.legacy_excerpt}…</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkEntOpen} onOpenChange={setBulkEntOpen}>
        <DialogContent className="max-w-md" data-testid="rb-bulk-ent-dialog">
          <DialogHeader><DialogTitle>Set entity applicability</DialogTitle><DialogDescription>Apply these entity types to the {sel.size} selected block{sel.size === 1 ? "" : "s"}. Choose “All”, or pick specific types (e.g. LLP only).</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-1.5" data-testid="rb-bulk-ent-picker">
            {[["all", "All"], ...ENTITY_TYPES].map(([v, l]) => {
              const on = v === "all" ? bulkEnts.length === 0 : bulkEnts.includes(v);
              return <button key={v} type="button" onClick={() => toggleBulkEnt(v)} data-testid={`rb-bulk-ent-${v}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-cass-navy text-white border-cass-navy" : "bg-white text-cass-slate border-border hover:border-cass-navy"}`}>{l}</button>;
            })}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBulkEntOpen(false)}>Cancel</Button><Button className="bg-cass-navy text-white" onClick={applyBulkEnts} data-testid="rb-bulk-ent-apply">Apply to {sel.size}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
